/**
 * @fileoverview Membership & Share Capital hub page.
 * @description Two clickable cards linking to this group's sub-pages (Member Count, Share Capital
 * Segmentation), each previewing that sub-page's own headline metric and YoY trend. Ported from
 * the InsightBox Banking design system's own `MembershipShareCapital/Grid.tsx` — the metric/trend
 * numbers are computed the same way here as this app's own filter model (local `fy`/`branchId`
 * state, not the reference's global FilterContext — see the Banking module plan's "explicit
 * deviations").
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  LATEST_FY,
  core,
  formatCr,
  formatNumber,
  formatSignedPct,
  resolveBankingAccessScope,
  resolveFy,
  trendLabel,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import PageHeader from '@/shared/components/molecules/PageHeader'
import ViewGridCard from '@/shared/components/molecules/ViewGridCard'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { FyFilter } from '@/features/banking'

/**
 * @component
 */
function BankingMembershipPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter] = useState<FyFilter>(LATEST_FY)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.membership.kicker')}
      title={t('nav.membershipShareCapital')}
      description={t('banking.membership.description')}
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
          <ViewGridCard
            to={ROUTES.BANKING_MEMBERSHIP_MEMBER_COUNT}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
          <ViewGridCard
            to={ROUTES.BANKING_MEMBERSHIP_SHARE_CAPITAL_SEGMENTATION}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
        </div>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const memberGrowth = yoyGrowth(fy, (f) => core(data, f, branchId).totalMembers)
  const capitalGrowth = yoyGrowth(fy, (f) => core(data, f, branchId).shareCapital)

  return (
    <div className="flex flex-col gap-4">
      {header}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
        <ViewGridCard
          to={ROUTES.BANKING_MEMBERSHIP_MEMBER_COUNT}
          title={t('banking.membership.memberCount.title')}
          description={t('banking.membership.memberCount.description')}
          metricLabel={t('banking.membership.memberCount.metricLabel')}
          metricValue={formatNumber(core(data, fy, branchId).totalMembers)}
          trend={{
            direction: trendLabel(memberGrowth),
            text:
              memberGrowth !== null
                ? t('banking.yoyTrend', { value: formatSignedPct(memberGrowth) })
                : t('banking.baseYear'),
          }}
        />
        <ViewGridCard
          to={ROUTES.BANKING_MEMBERSHIP_SHARE_CAPITAL_SEGMENTATION}
          title={t('banking.membership.shareCapitalSegmentation.title')}
          description={t('banking.membership.shareCapitalSegmentation.description')}
          metricLabel={t('banking.membership.shareCapitalSegmentation.metricLabel')}
          metricValue={formatCr(core(data, fy, branchId).shareCapital)}
          trend={{
            direction: trendLabel(capitalGrowth),
            text:
              capitalGrowth !== null
                ? t('banking.yoyTrend', { value: formatSignedPct(capitalGrowth) })
                : t('banking.baseYear'),
          }}
        />
      </div>
    </div>
  )
}

export default BankingMembershipPage
